'use client';
import { useEffect, useRef, useState } from 'react';
import {
  SectLabel, SectH2, SectLede, GlassCard,
  V4CtaButton, V4GhostButton,
} from '@/components/landing/v4-shared';
import { PhoneShell } from '@/components/landing/PhoneShell';

const DIAL_SEQUENCE = '**21*5551234567#';
// Flat single-character keys, matching the approved mockup's
// `.dialer__keys span` (docs/design/mockups/homepage-v5-mockup.html).
const KEYPAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

/** Loops through: type sequence → success screen → reset. Starts on scroll into view. */
function useDialerLoop() {
  const phoneRef = useRef<HTMLDivElement>(null);
  const displayRef = useRef<HTMLSpanElement>(null);
  const keyRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const startedRef = useRef(false);

  useEffect(() => {
    const phoneEl = phoneRef.current;
    const displayEl = displayRef.current;
    if (!phoneEl || !displayEl) return;

    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const schedule = (fn: () => void, ms: number) => {
      const id = setTimeout(fn, ms);
      timeouts.push(id);
    };

    const runLoop = () => {
      let i = 0;
      displayEl.textContent = '';
      phoneEl.classList.remove('v4-sp-success-state');

      const typeNext = () => {
        if (i >= DIAL_SEQUENCE.length) {
          schedule(() => {
            phoneEl.classList.add('v4-sp-success-state');
            schedule(runLoop, 4200);
          }, 600);
          return;
        }
        const ch = DIAL_SEQUENCE[i];
        displayEl.textContent = (displayEl.textContent ?? '') + ch;
        const key = keyRefs.current.get(ch);
        if (key) {
          key.classList.add('v4-sp-hit');
          schedule(() => key.classList.remove('v4-sp-hit'), 350);
        }
        i += 1;
        schedule(typeNext, 280);
      };
      typeNext();
    };

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            runLoop();
            obs.disconnect();
          }
        });
      },
      { threshold: 0.2 },
    );
    obs.observe(phoneEl);

    return () => {
      obs.disconnect();
      timeouts.forEach(clearTimeout);
    };
  }, []);

  return {
    phoneRef,
    displayRef,
    registerKey: (k: string) => (el: HTMLDivElement | null) => {
      if (el) keyRefs.current.set(k, el);
      else keyRefs.current.delete(k);
    },
  };
}

export default function SimpleSetupSection() {
  const { phoneRef, displayRef, registerKey } = useDialerLoop();
  const [displaySeed] = useState(DIAL_SEQUENCE); // initial render value before JS mounts
  return (
    <section
      id="simple"
      data-v4-section="simple-setup"
      aria-labelledby="simple-setup-heading"
      style={{
        background: 'radial-gradient(ellipse 80% 50% at 20% 50%, rgba(34,211,238,0.05), transparent 70%)',
        position: 'relative',
      }}
    >
      <div className="v4-section">
        {/* Two-column grid */}
        <div
          className="v4-simple-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '1.1fr 1fr',
            gap: '72px',
            alignItems: 'center',
          }}
        >
          {/* Copy column (left) */}
          <div>
            <SectLabel color="var(--cyan-glow)">● Pillar 1 — Simple Setup</SectLabel>
            <SectH2 id="simple-setup-heading">
              Forward your number. AI answers every call. Your business{' '}
              <em style={{ fontFamily: 'var(--font-instrument)', fontStyle: 'italic', color: 'var(--cyan-glow)', fontWeight: 400 }}>
                books more appointments
              </em>.
            </SectH2>
            <SectLede>
              Most small businesses miss <strong style={{ color: 'var(--paper)', fontWeight: 600 }}>62% of after-hours calls</strong>. BizRnR answers every one of them — in your business voice, 24/7/365, starting at $99/month.
            </SectLede>

            {/* 3 numbered steps */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { num: '1', title: 'Sign up', body: '60 seconds. 30-day guarantee.' },
                { num: '2', title: 'Forward your number', body: 'One short code. Any carrier.' },
                { num: '3', title: 'Book more appointments', body: 'Every call answered, every lead booked.' },
              ].map((step) => (
                <GlassCard key={step.num} hoverable accent="cyan" radius={14} padding={20}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        border: '1px solid rgba(125,250,255,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontFamily: 'var(--font-instrument)',
                        fontStyle: 'italic',
                        fontSize: 20,
                        color: 'var(--cyan-glow)',
                      }}
                    >
                      {step.num}
                    </div>
                    <div>
                      <div style={{ color: 'var(--paper)', fontSize: 16, fontWeight: 500, fontFamily: 'var(--font-geist-sans)' }}>
                        {step.title}
                      </div>
                      <div style={{ color: 'var(--dim)', fontSize: 14, marginTop: 4, maxWidth: '42ch', fontFamily: 'var(--font-geist-sans)' }}>
                        {step.body}
                      </div>
                    </div>
                  </div>
                </GlassCard>
              ))}
            </div>

            {/* CTAs */}
            <div style={{ display: 'flex', gap: '16px', marginTop: '32px', flexWrap: 'wrap' }}>
              <V4CtaButton href="/auth/register" size="lg">Get started</V4CtaButton>
              <V4GhostButton href="/simple-setup" variant="cyan" size="lg">See Simple Setup</V4GhostButton>
            </div>
          </div>

          {/* Visual panel (right) — realistic mini iPhone keypad dialer,
              decorative. Chrome (frame/status-bar/island pill/home-bar) is
              owned by PhoneShell; this component only fills the island and
              screen slots. */}
          <div
            className="v4-sp-wrap"
            aria-hidden="true"
            style={{ animation: 'v4-fadeUp 0.8s 0.3s both ease-out' }}
          >
            <PhoneShell
              size="mini"
              islandContent={
                <>
                  <span className="v4-sp-island-lens" aria-hidden="true" />
                  <span className="v4-sp-island-status">
                    Keypad · <em>forwarding</em>
                  </span>
                </>
              }
            >
              <div ref={phoneRef} className="v4-sp-screen">
                <p className="v4-sp-label">Dial this once · ● 30 sec · any US carrier</p>
                <div className="v4-sp-display">
                  <span ref={displayRef}>{displaySeed}</span>
                  <span className="v4-sp-caret" />
                </div>
                <div className="v4-sp-keypad">
                  {KEYPAD.map((key) => (
                    <div
                      key={key}
                      ref={registerKey(key)}
                      className="v4-sp-key"
                      data-k={key}
                    >
                      {key}
                    </div>
                  ))}
                </div>
                <div className="v4-sp-call">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.21 2.2z" />
                  </svg>
                </div>

                {/* Forwarding-active success overlay */}
                <div className="v4-sp-success">
                  <div className="v4-sp-success-pulses">
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="v4-sp-success-check">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M5 12l5 5L20 7"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="v4-sp-success-title">Forwarding active</div>
                  <div className="v4-sp-success-sub">
                    <span>All calls</span>
                    <span className="v4-sp-arrow">→</span>
                    <span>Bri</span>
                  </div>
                </div>
              </div>
            </PhoneShell>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 1100px) {
          .v4-simple-grid { grid-template-columns: 1fr !important; }
        }
        .v4-cta-primary:focus-visible,
        .v4-cta-ghost:focus-visible {
          outline: 2px solid var(--cyan-glow);
          outline-offset: 3px;
        }
      `}</style>
    </section>
  );
}
